import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";
import CommonToast from "../../../../components/_utils/CommonToast";
import ICONS from "../../../../assets/Icons";

const baseUrl = env.REACT_APP_BASE_URL;

export const beneTxnModalAccount = createAsyncThunk(
  "beneTxnModalAccount",
  async (values, { rejectWithValue }) => {
    const { page_no, page_size, search_string, type } = values;
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/beneficiary`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          params: {
            page_no: page_no,
            page_size: page_size,
            search_string: search_string,
            internal_account: type === "internalAccount" ? "Y" : "N",
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const beneTxnOwnerAccounts = createAsyncThunk(
  "beneTxnOwnerAccounts",
  async (values, { rejectWithValue }) => {
    const {
      page_no,
      approval_status,
      categoryName,
      page_size,
      search_string,
      bank_name,
      sort_by_balance,
    } = values || {};

    try {
      const response = await axiosInstance.get(`${baseUrl}/accounts/owner`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          "page_no": page_no,
          "page_size": page_size,
          "approval_status": approval_status,
          "category": categoryName,
          "search_string": search_string,
          "connected_banking": "Y",
          "bank_name": bank_name,
          "sort_by_balance[balance]": sort_by_balance
            ? sort_by_balance === "Low to high"
              ? "ascending"
              : "descending"
            : undefined,
        },
      });
      return response.data;
    } catch (error) {
      CommonToast({
        text: error?.response?.data?.error,
        icon: ICONS.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(error);
    }
  },
);

export const beneTxnOwnerAccountsNonActive = createAsyncThunk(
  "beneTxnOwnerAccountsNonActive",
  async (values, { rejectWithValue }) => {
    const {
      page_no,
      approval_status,
      categoryName,
      page_size,
      search_string,
      bank_name,
      sort_by_balance,
    } = values || {};

    try {
      const response = await axiosInstance.get(`${baseUrl}/accounts/owner`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          "page_no": page_no,
          "page_size": page_size,
          "approval_status": approval_status,
          "category": categoryName,
          "search_string": search_string,
          "connected_banking": "N", // Non-active accounts
          "bank_name": bank_name,
          "sort_by_balance[balance]": sort_by_balance
            ? sort_by_balance === "Low to high"
              ? "ascending"
              : "descending"
            : undefined,
        },
      });
      return response.data;
    } catch (error) {
      CommonToast({
        text: error?.response?.data?.error,
        icon: ICONS.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(error);
    }
  },
);

export const beneCreateTxn = createAsyncThunk(
  "beneCreateTxn",
  async (values, { rejectWithValue }) => {
    const {
      amount,
      owner_account_id,
      beneficiary_account_id,
      category,
      additional_data,
      payment_method,
      confidential_transaction,
      labels,
    } = values;

    const requestBody = {
      amount: amount,
      owner_account_id: owner_account_id,
      beneficiary_account_id: beneficiary_account_id,
      category: category,
      additional_data: {
        data: additional_data,
      },
      payment_method: payment_method,
      confidential_transaction: confidential_transaction,
      labels: labels,
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": sessionStorage.getItem("q2p_token"),
      },
    };

    try {
      const response = await axiosInstance.post(
        `${baseUrl}/transactions/beneficiary`,
        requestBody,
        config,
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        const responseData = error?.response?.data;

        return rejectWithValue(responseData);
      } else {
        return rejectWithValue(error);
      }
    }
  },
);

export const beneTxnSuccessDetail = createAsyncThunk(
  "beneTxnSuccessDetail",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        throw new Error("Invalid beneficiary id");
      }
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/beneficiary/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const refactorBeneficiaryTxn = createAsyncThunk(
  "transactions/refactorBeneficiary",
  async ({ txn_ids }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/beneficiary/refactor`,
        { txn_id: txn_ids }, // Example: txn_ids = ['1234-uuid']
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const beneTransactionAccounts = createSlice({
  name: "beneTransactionAccounts",
  initialState: {
    beneOwnerData: null,
    beneOwnerLoading: null,
    beneOwnerError: null,

    refactorStatus: null,
    loading: false,
    error: null,

    beneOwnerDataNonActive: null,
    beneOwnerLoadingNonActive: null,
    beneOwnerErrorNonActive: null,

    beneTxnAccountData: null,
    beneTxnAccountLoading: null,
    beneTxnAccountError: null,

    beneCreateTxnData: null,
    beneCreateTxnLoading: null,
    beneCreateTxnError: null,

    beneTxnSuccessData: null,
    beneTxnSuccessDataError: null,
    beneTxnSuccessLoading: null,
    beneTxnSuccessError: null,
  },
  reducers: {
    resetBeneOwnerData(state) {
      state.beneOwnerData = null;
    },
    resetBeneOwnerDataNonActive(state) {
      state.beneOwnerDataNonActive = null;
    },
    resetTxnError(state) {
      state.beneCreateTxnError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(beneTxnOwnerAccounts.pending, (state, action) => {
      state.beneOwnerLoading = true;
    });
    builder.addCase(beneTxnOwnerAccounts.fulfilled, (state, action) => {
      state.beneOwnerLoading = false;
      state.beneOwnerData = action.payload;
    });
    builder.addCase(beneTxnOwnerAccounts.rejected, (state, action) => {
      state.beneOwnerError = true;
      state.beneOwnerLoading = false;
    });

    builder.addCase(refactorBeneficiaryTxn.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.refactorStatus = null;
    });
    builder.addCase(refactorBeneficiaryTxn.fulfilled, (state, action) => {
      state.loading = false;
      state.refactorStatus = action.payload;
    });
    builder.addCase(refactorBeneficiaryTxn.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(beneTxnOwnerAccountsNonActive.pending, (state, action) => {
      state.beneOwnerLoadingNonActive = true;
    });
    builder.addCase(
      beneTxnOwnerAccountsNonActive.fulfilled,
      (state, action) => {
        state.beneOwnerLoadingNonActive = false;
        state.beneOwnerDataNonActive = action.payload;
      },
    );
    builder.addCase(beneTxnOwnerAccountsNonActive.rejected, (state, action) => {
      state.beneOwnerErrorNonActive = true;
      state.beneOwnerLoadingNonActive = false;
    });

    builder.addCase(beneTxnModalAccount.pending, (state) => {
      state.beneTxnAccountLoading = true;
    });
    builder.addCase(beneTxnModalAccount.fulfilled, (state, action) => {
      state.beneTxnAccountLoading = false;
      state.beneTxnAccountData = action.payload;
    });
    builder.addCase(beneTxnModalAccount.rejected, (state, action) => {
      state.beneTxnAccountError = true;
      state.beneTxnAccountLoading = false;
      state.beneTxnAccountData = action.payload;
    });

    builder.addCase(beneCreateTxn.pending, (state, action) => {
      state.beneCreateTxnLoading = true;
    });
    builder.addCase(beneCreateTxn.fulfilled, (state, action) => {
      state.beneCreateTxnLoading = false;
      state.beneCreateTxnData = action.payload;
    });
    builder.addCase(beneCreateTxn.rejected, (state, action) => {
      state.beneCreateTxnLoading = false;
      state.beneCreateTxnError = action.payload;
      state.beneCreateTxnData = null;
    });

    builder.addCase(beneTxnSuccessDetail.pending, (state, action) => {
      state.beneTxnSuccessLoading = true;
    });
    builder.addCase(beneTxnSuccessDetail.fulfilled, (state, action) => {
      state.beneTxnSuccessLoading = false;
      state.beneTxnSuccessDataError = null;
      state.beneTxnSuccessData = action.payload;
    });
    builder.addCase(beneTxnSuccessDetail.rejected, (state, action) => {
      state.beneTxnSuccessDataError = action.payload;
      state.beneTxnSuccessError = true;
      state.beneTxnSuccessData = null;
    });
  },
});

export const {
  resetVerification,
  resetPANVerification,
  resetBankVerification,
  resetBeneBankData,
  resetBeneOwnerData,
  resetBeneOwnerDataNonActive,
  resetTxnError,
} = beneTransactionAccounts.actions;

export default beneTransactionAccounts.reducer;
